"use client";

import { useCallback } from "react";
import type { MutableRefObject } from "react";
import type { TeamDetail } from "@cabal/shared/domain/session/team/types.js";
import {
  MobileModalDebugEvent,
  type PersistentDebugLogger,
  captureMobileModalEnvironment,
} from "@/hooks/telemetry";
import {
  TEAM_MODAL_CENTER_ORIGIN,
  TeamModalMode,
  useTeamModal,
} from "@/contexts/team-modal";
import { useSessionStartChooser } from "@/contexts/session-start-chooser";
import { runOpenDeployForTeam } from "./homePageActions";
import type {
  HomePageInteractionState,
  HomePageModalStateLocal,
} from "./domain/home-page/types";

type OpenAuthModal = HomePageModalStateLocal["openAuthModal"];
type ShowTeamDetailsModal = HomePageModalStateLocal["showTeamDetailsModal"];
type ShowTeamsModal = HomePageModalStateLocal["showTeamsModal"];
type TeamDetailsTeamId = HomePageModalStateLocal["teamDetailsTeamId"];

interface HomePagePreflightLaunchHandlersLocal {
  handleStartNewSession: () => void;
  /** Resolves once the gate is up — signed out, that is after the deploy
   *  intent is parked. */
  openDeployForTeam: (teamId: string) => Promise<void>;
}

interface UseHomePagePreflightLaunchHandlersInput {
  latestInteractionState: MutableRefObject<HomePageInteractionState>;
  modalDebugLogger: PersistentDebugLogger;
  modalState: Pick<HomePageModalStateLocal, "openAuthModal">;
}

interface UseOpenDeployForTeamHandlerInput {
  latestInteractionState: MutableRefObject<HomePageInteractionState>;
  modalDebugLogger: PersistentDebugLogger;
  openAuthModal: OpenAuthModal;
}

interface UseReplicateTeamHandlerInput {
  modalDebugLogger: PersistentDebugLogger;
  showTeamDetailsModal: ShowTeamDetailsModal;
  showTeamsModal: ShowTeamsModal;
  teamDetailsTeamId: TeamDetailsTeamId;
}

interface ReadInteractionStateInput {
  latestInteractionState: MutableRefObject<HomePageInteractionState>;
}

function readInteractionState({
  latestInteractionState,
}: ReadInteractionStateInput): HomePageInteractionState {
  return latestInteractionState.current;
}

/**
 * "Start new session" with no team context opens the session-start chooser
 * overlay (ruled 2026-08-29 — blank deploy does not exist). The chooser is a
 * menu, so it opens with NO auth gate; its options gate themselves. Deploy of
 * an EXISTING team stays gate-before-open into the canonical team modal.
 */
export function useHomePagePreflightLaunchHandlers({
  latestInteractionState,
  modalDebugLogger,
  modalState,
}: UseHomePagePreflightLaunchHandlersInput): HomePagePreflightLaunchHandlersLocal {
  const { openAuthModal } = modalState;
  const { openSessionStartChooser } = useSessionStartChooser();

  const openDeployForTeam = useOpenDeployForTeamHandler({
    latestInteractionState,
    modalDebugLogger,
    openAuthModal,
  });

  return { handleStartNewSession: openSessionStartChooser, openDeployForTeam };
}

export function useHomePageReplicateTeamHandler({
  modalDebugLogger,
  showTeamDetailsModal,
  showTeamsModal,
  teamDetailsTeamId,
}: UseReplicateTeamHandlerInput): (teamConfig: TeamDetail) => void {
  const { openExisting } = useTeamModal();

  return useCallback(
    (teamConfig: TeamDetail) => {
      modalDebugLogger.logEventFlow({
        name: MobileModalDebugEvent.ReplicateTeamFromSessionHistory,
        payload: {
          teamId: teamConfig.id,
          showTeamsModal,
          showTeamDetailsModal,
          teamDetailsTeamId,
          environment: captureMobileModalEnvironment(),
        },
      });
      openExisting({
        teamId: teamConfig.id,
        mode: TeamModalMode.Deploy,
        origin: TEAM_MODAL_CENTER_ORIGIN,
      });
    },
    [
      modalDebugLogger,
      openExisting,
      showTeamDetailsModal,
      showTeamsModal,
      teamDetailsTeamId,
    ],
  );
}

function useOpenDeployForTeamHandler({
  latestInteractionState,
  modalDebugLogger,
  openAuthModal,
}: UseOpenDeployForTeamHandlerInput): (teamId: string) => Promise<void> {
  const { openExisting } = useTeamModal();

  return useCallback(
    async (teamId: string): Promise<void> => {
      await runOpenDeployForTeam({
        interactionState: readInteractionState({ latestInteractionState }),
        modalDebugLogger,
        openAuthModal,
        openDeployModal: () =>
          openExisting({
            teamId,
            mode: TeamModalMode.Deploy,
            origin: TEAM_MODAL_CENTER_ORIGIN,
          }),
        teamId,
      });
    },
    [
      latestInteractionState,
      modalDebugLogger,
      openAuthModal,
      openExisting,
    ],
  );
}
